import classNames from 'classnames/bind';
import React from 'react';

import { Button } from '@components/Button/Button';
import { Decoration } from '@components/Decoration/Decoration';
import { Section } from '@components/Section/Section';
import { Text } from '@components/Text/Text';
import { showLiveChat } from '@containers/LiveChat/LiveChat';
import { externalRoutes, routes } from '@routes';
import { sales, support } from '@siteData';

import styles from './ContactBoxes.module.scss';
import ChatIcon from '@icons/chat.svg';
import EnvelopeIcon from '@icons/envelope.svg';

const cx = classNames.bind(styles);

export const ContactBoxes: React.FC = () => (
  <Decoration className={styles.decoration} position="top-left" size="lg" theme="blue">
    <Section borderRadius="rounded" className={styles.section} padding={0} theme="dark">
      <div className={styles.wrapper}>
        <article className={cx('box', 'primary')}>
          <div className={styles.inner}>
            <header className={styles.header}>
              <Text align="left" className="mb-4" color="white" size={44} tag="h2">
                Client Solutions
              </Text>
              <Text color="white" opacity={70} tag="p">
                Want to discuss technical details of your use case and pricing?
              </Text>
            </header>
            {[
              {
                icon: <EnvelopeIcon />,
                label: sales.email,
                to: `mailto:${sales.email}`,
                moreInfo: 'Replies in 1 business day',
              },
            ].map(({ icon, to, label, moreInfo }) => (
              <div key={label} className={styles.linkWrapper}>
                <div className={styles.iconWrapper}>{icon}</div>
                <div>
                  <Button href={to} spacing="lg" variant="inverted">
                    {label}
                  </Button>
                  {moreInfo && (
                    <Text
                      className="ml-2"
                      color="white"
                      display="inline"
                      opacity={70}
                      size={14}
                      tag="p"
                    >
                      {moreInfo}
                    </Text>
                  )}
                </div>
              </div>
            ))}
            <hr className={styles.separator} />
            <div className={styles.footer}>
              <Button href={routes.getStarted} size="md" theme="button" variant="inverted">
                Talk to sales
              </Button>
            </div>
          </div>
        </article>

        <article className={cx('box', 'secondary')}>
          <div className={styles.inner}>
            <header className={styles.header}>
              <Text align="left" className="mb-4" color="white" size={44} tag="h2">
                Tech Support
              </Text>
              <Text color="white" opacity={70} tag="div">
                <Text tag="p">In need of assistance? We're here to help 24/7.</Text>
                <Text tag="p">
                  For API docs & FAQ visit our{' '}
                  <Button
                    className={styles.link}
                    href={externalRoutes.client.knowledgeBase}
                    variant="inherit"
                    external
                    noIcon
                  >
                    knowledge base
                  </Button>
                  .
                </Text>
              </Text>
            </header>

            {[
              {
                icon: <EnvelopeIcon />,
                label: support.email,
                to: `mailto:${support.email}`,
              },
            ].map(({ icon, to, label }) => (
              <div key={label} className={styles.linkWrapper}>
                <div className={styles.iconWrapper}>{icon}</div>
                <div>
                  <Button className={styles.contactLink} href={to} spacing="lg" variant="inverted">
                    {label.replace('(0)', '')}
                  </Button>
                </div>
              </div>
            ))}
            <hr className={styles.separator} />
            <div className={styles.footer}>
              <Button
                className={styles.button}
                icon={<ChatIcon />}
                iconPosition="before"
                size="md"
                theme="button"
                variant="inverted"
                onClick={showLiveChat}
              >
                Chat now
              </Button>
              <Text color="white" opacity={70} size={14} tag="p">
                Typically replies in &lt; 1min
              </Text>
            </div>
          </div>
        </article>
      </div>
    </Section>
  </Decoration>
);
